import React, { useState, FunctionComponent } from "react"
import { Button, Icon } from "@kuali/pure-ui"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import { postHTML } from "./update-editor-request"
import "./styles.scss"

interface Props {
  htmlValue: string
  defaultValue?: string
  inputName: string
  isInline: boolean
}

const QuillEditor: FunctionComponent<Props> = ({
  htmlValue = "",
  inputName = "",
  defaultValue = "",
  isInline = true
}) => {
  const [value, setValue] = useState(htmlValue)
  const [isLoadingUpdateResponse, setIsLoadingUpdateResponse] = useState(true)
  const [isUpdated, setIsUpdated] = useState(false)

  const modules = {
    toolbar: [
      [{ header: [3, 4, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link"],
      ["clean"]
    ]
  }
  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link"
  ]

  const postEditor = async (data: string) => {
    setIsLoadingUpdateResponse(true)
    const result = await postHTML(data, inputName)

    if (result) {
      setIsLoadingUpdateResponse(false)
      if (result.isSuccess) {
        setIsUpdated(true)
      } else {
        setIsUpdated(false)
      }
    }
  }

  const renderQuill = (htmlValue) => {
    return (
      <div className="editor-wrapper">
        <ReactQuill
          theme="snow"
          value={value}
          modules={modules}
          formats={formats}
          onChange={(newValue: string) => {
            setIsLoadingUpdateResponse(true)
            setValue(newValue)
          }}
        />
      </div>
    )
  }

  const renderStandaloneEditor = () => {
    return (
      <React.Fragment>
        {/* We keep a hidden input synced with the Quill editor so it gets submitted with the form post
      to Rails, hopefully won't be needed when we switch modals to pure React */}
        <input
          type="hidden"
          value={value ? value : ""}
          name={inputName + "[content]"}
        />
        {renderQuill(value)}
      </React.Fragment>
    )
  }

  const renderInlineEditor = () => {
    return (
      <React.Fragment>
        {/* We keep a hidden input synced with the Quill editor so it gets submitted with the form post
          to Rails, hopefully won't be needed when we switch modals to pure React */}
        <input type="hidden" value={value ? value : ""} name={inputName} />
        {renderQuill(value)}
        <div className="editor-footer">
          <Button
            size="large"
            kind="transparent"
            shadow={true}
            block={false}
            onClick={(event) => {
              event.preventDefault()
              setValue(defaultValue)
            }}
          >
            Restore Default Text
          </Button>
          <Button
            size="large"
            kind="solid"
            shadow={true}
            block={false}
            onClick={(event) => {
              event.preventDefault()
              postEditor(value)
            }}
          >
            Update
          </Button>
          {!isLoadingUpdateResponse && isUpdated && (
            <div className="editor-status">
              <p role="region" aria-live="polite">
                <Icon name="check" /> Your content has been successfully updated
              </p>
            </div>
          )}
          {!isLoadingUpdateResponse && !isUpdated && (
            <div className="editor-status">
              <p role="region" aria-live="polite">
                <Icon name="alert-warning" /> Your content could not be updated.
                Please try again later or contact support.
              </p>
            </div>
          )}
        </div>
      </React.Fragment>
    )
  }

  if (isInline) {
    return renderInlineEditor()
  } else {
    return renderStandaloneEditor()
  }
}

export default QuillEditor
